import { useState } from "react";
import { PolicyTypeDialog } from "./PolicyTypeDialog";
import { usePolicyTypes } from "./hooks/usePolicyTypes";
import { PolicyType } from "./types";
import { usePolicyLines } from "@/hooks/usePolicyLines";
import { useToast } from "@/hooks/use-toast";
import { usePolicyTypeOperations } from "./usePolicyTypeOperations";
import { PolicyTypeHeader } from "./components/PolicyTypeHeader";
import { PolicyTypeTable } from "./components/PolicyTypeTable";
import { arrayMove } from "@dnd-kit/sortable";

export const PolicyTypesContainer = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPolicyType, setSelectedPolicyType] = useState<PolicyType | null>(null);
  const { policyTypes, refetch, updatePolicyTypeOrder } = usePolicyTypes();
  const { deletePolicyType } = usePolicyTypeOperations();
  const { data: policyLines, isLoading } = usePolicyLines();
  const { toast } = useToast();

  const handleEdit = (policyType: PolicyType) => {
    setSelectedPolicyType(policyType);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    const result = await deletePolicyType(id);
    if (result.success) {
      toast({
        title: "Success",
        description: "Policy type deleted successfully",
      });
      refetch();
    } else {
      toast({
        title: "Error",
        description: "Failed to delete policy type",
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = policyTypes.findIndex((item) => item.id === active.id);
      const newIndex = policyTypes.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(policyTypes, oldIndex, newIndex);
      const result = await updatePolicyTypeOrder(newItems);
      
      if (!result.success) {
        toast({
          title: "Error",
          description: "Failed to update policy type order",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      <PolicyTypeHeader onAddClick={() => setIsDialogOpen(true)} />

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        policyLines?.map((policyLine) => (
          <div key={policyLine.id} className="space-y-4">
            <h3 className="text-lg font-semibold">{policyLine.name}</h3>
            <PolicyTypeTable
              policyTypes={policyTypes.filter(pt => pt.policy_line_id === policyLine.id)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDragEnd={handleDragEnd}
            />
          </div>
        ))
      )}

      <PolicyTypeDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        selectedPolicyType={selectedPolicyType}
        onClose={() => {
          setSelectedPolicyType(null);
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </div>
  );
};